<template>
  <div class="app">
    <!-- Conference booth demo — locked to AccessIQ DEMO kiosk session -->
    <div v-if="showAppChrome && isConferenceMode" class="conference-topbar">
      <i class="fas fa-store"></i>
      Conference demo — <strong>AccessIQ DEMO</strong>
      <span class="conference-booth-links">
        <a :href="boothToolUrl('/booth-app.html')">Menu</a>
        <a :href="boothToolUrl('/login')">Visitor login</a>
        <a :href="boothToolUrl('/booth-staff.html')">Sign-ups</a>
        <a :href="boothToolUrl('/visit.html')">Sign-up form</a>
        <a :href="boothToolUrl('/accessiq-qr.html')">QR</a>
      </span>
      <button class="conference-next-btn" type="button" @click="handleNextVisitor">
        <i class="fas fa-user-plus"></i> Next visitor
      </button>
    </div>

    <!-- View As Client — persistent top bar, spans full width above everything -->
    <div
      v-if="showAppChrome && assumedOrg && !isConferenceMode"
      class="view-as-topbar"
      :class="{ 'view-as-topbar--demo': isDemoAssumedOrg }"
    >
      <i class="fas fa-eye"></i>
      Viewing as <strong>{{ assumedOrg.name }}</strong>
      <span class="view-as-role-chip">{{ assumedRoleLabel }}</span>
      <template v-if="isDemoAssumedOrg">
        <span class="demo-divider" aria-hidden="true" />
        <label class="demo-field">
          State
          <select
            class="demo-select"
            :value="demoContextState.state"
            aria-label="Demo state"
            @change="onDemoStateChange"
          >
            <option v-for="s in DEMO_STATES" :key="s.code" :value="s.code">{{ s.name }}</option>
          </select>
        </label>
        <label class="demo-field">
          Company
          <select
            class="demo-select"
            :value="demoContextState.companyType"
            aria-label="Demo company type"
            @change="onDemoCompanyChange"
          >
            <option v-for="c in DEMO_COMPANY_TYPES" :key="c.id" :value="c.id">{{ c.label }}</option>
          </select>
        </label>
      </template>
      <button class="view-as-exit-btn" @click="openExitModal">
        <i class="fas fa-lock"></i> Exit
      </button>
    </div>

    <NavSidebar v-if="showAppChrome" />
    <main
      class="main-content"
      :class="{
        'no-sidebar': !showAppChrome,
        'has-view-as-bar': showAppChrome && assumedOrg && !isConferenceMode,
        'has-conference-bar': showAppChrome && isConferenceMode,
      }"
    >
      <ErrorBoundary>
        <router-view v-slot="{ Component }">
          <keep-alive include="IQHubView">
            <component :is="Component" v-if="Component" :key="pageInstanceKey" />
          </keep-alive>
        </router-view>
      </ErrorBoundary>
    </main>
    <PollieWidget v-if="showAppChrome && !isConferenceMode" />

    <!-- Inactivity warning modal -->
    <div v-if="showInactivityWarning" class="inactivity-overlay">
      <div class="inactivity-modal" role="dialog" aria-modal="true" aria-labelledby="inactivity-title">
        <div class="inactivity-header">
          <i class="fas fa-clock"></i>
          <span id="inactivity-title">Still there?</span>
        </div>
        <p class="inactivity-body">
          You've been inactive for 50 minutes. You'll be logged out in 10 minutes unless you stay active.
        </p>
        <div class="inactivity-actions">
          <button class="inactivity-stay-btn" @click="stayLoggedIn">
            Stay logged in
          </button>
        </div>
      </div>
    </div>

    <!-- Demo exit password modal -->
    <div v-if="showExitModal" class="demo-exit-overlay" @click.self="cancelExit">
      <div class="demo-exit-modal" role="dialog" aria-modal="true">
        <div class="demo-exit-header">
          <i class="fas fa-lock"></i>
          <span>Exit demo view</span>
        </div>
        <p class="demo-exit-body">
          Enter your password to exit the <strong>{{ assumedOrg && assumedOrg.name }}</strong> view and return to your own account.
        </p>
        <input
          ref="exitPasswordInput"
          v-model="exitPassword"
          type="password"
          class="demo-exit-input"
          placeholder="Your account password"
          autocomplete="current-password"
          @keydown.enter="confirmExit"
          @keydown.esc="cancelExit"
        />
        <p v-if="exitError" class="demo-exit-error">
          <i class="fas fa-exclamation-circle"></i> {{ exitError }}
        </p>
        <div class="demo-exit-actions">
          <button class="demo-exit-cancel" @click="cancelExit">
            Cancel
          </button>
          <button class="demo-exit-confirm" :disabled="!exitPassword" @click="confirmExit">
            Exit demo view
          </button>
        </div>
      </div>
    </div>

    <!-- Build-4 / 4C: global API outage recovery — assessed ETA from deploy -->
    <div
      v-if="showReconnectingOverlay"
      class="reconnect-overlay"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="reconnect-title"
      aria-live="polite"
    >
      <div class="reconnect-modal">
        <div class="reconnect-header">
          <i class="fas fa-sync-alt reconnect-spinner" aria-hidden="true"></i>
          <span id="reconnect-title">Reconnecting…</span>
        </div>
        <p class="reconnect-body">
          AccessIQ is updating. This should take about {{ reconnectEtaMinutes }} minute{{ reconnectEtaMinutes === 1 ? '' : 's' }}.
          This page will reload with the new version when the update is done.
        </p>
        <button
          v-if="reconnectTimedOut"
          type="button"
          class="reconnect-refresh-btn"
          @click="refreshForOutage"
        >
          Refresh page
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import NavSidebar from '@/components/NavSidebar.vue'
import ErrorBoundary from '@/components/ErrorBoundary.vue'
import PollieWidget from '@/components/PollieWidget.vue'
import auth from '@/services/auth'
import whitelabelBranding from '@/services/whitelabelBrandingService'
import { assumedOrgState, clearAssumedOrg, restoreAssumedOrg } from '@/services/assumedOrg'
import {
  DEMO_COMPANY_TYPES,
  DEMO_STATES,
  demoContextState,
  isDemoOrg,
  setDemoContext,
} from '@/services/demoContext'
import { isConferenceMode, startNextVisitor } from '@/services/conferenceMode'
import { subscribeOutageState, runWithOutageRetry } from '@/services/backendHealth'
import { runBeforeSessionLogout, safeAppReturnPath } from '@/utils/safeAppReturnPath'

const INACTIVITY_WARNING_MS = 50 * 60 * 1000;  // 50 minutes
const INACTIVITY_LOGOUT_MS  = 60 * 60 * 1000;  // 60 minutes

const ROLE_LABELS = {
  ORG_ADMIN: 'Org Admin',
  COMPLIANCE_ADMIN: 'Risk Analyst',
  VIEWER: 'Viewer',
};

export default {
  name: 'App',
  components: {
    NavSidebar,
    ErrorBoundary,
    PollieWidget,
  },

  data() {
    return {
      showExitModal: false,
      exitPassword: '',
      exitError: '',
      showInactivityWarning: false,
      showReconnectingOverlay: false,
      reconnectEtaMinutes: 6,
      reconnectTimedOut: false,
      DEMO_STATES,
      DEMO_COMPANY_TYPES,
      demoContextState,
    };
  },

  created() {
    this.inactivityTimerId = null;
    this.warningTimerId = null;
    this.boundResetInactivity = null;
    this.unsubscribeOutageState = null;
  },
  
  async mounted() {
    // Restore any assumed org session that survived a page refresh
    restoreAssumedOrg();

    // Initialize whitelabel branding on app mount
    try {
      await runWithOutageRetry(() => whitelabelBranding.initialize());
    } catch (error) {
      console.error('⚠️ Failed to initialize branding:', error);
    }

    // Start inactivity tracking if already authenticated on mount
    if (auth.isAuthenticated()) {
      this._startInactivityTracking();
    }

    this.unsubscribeOutageState = subscribeOutageState((state) => {
      this.showReconnectingOverlay = !!state.active;
      this.reconnectEtaMinutes = state.etaMinutes || 6;
      this.reconnectTimedOut = !!state.timedOut;
    });
  },

  beforeUnmount() {
    this._stopInactivityTracking();
    if (this.unsubscribeOutageState) {
      this.unsubscribeOutageState();
      this.unsubscribeOutageState = null;
    }
  },
  
  computed: {
    isAuthenticated() {
      this.$route.path;
      if (this.$route.path.includes('/secure-upload/')) {
        return false;
      }
      return auth.isAuthenticated();
    },
    isPrintStandalone() {
      return Boolean(this.$route.meta?.printStandalone);
    },
    assumedOrg() {
      return assumedOrgState.org;
    },
    isDemoAssumedOrg() {
      return isDemoOrg(assumedOrgState.org);
    },
    assumedRoleLabel() {
      return ROLE_LABELS[assumedOrgState.role] || assumedOrgState.role || '';
    },
    isConferenceMode() {
      return isConferenceMode();
    },
    pageInstanceKey() {
      if (this.$route.path === '/iq-hub') return 'iq-hub';
      return this.$route.fullPath;
    },
    /** Hide sidebar/top bars on login/register and public member pages. */
    showAppChrome() {
      if (!this.isAuthenticated) return false;
      if (this.isPrintStandalone) return false;
      const bareAuthPaths = ['/login', '/register', '/forgot-password'];
      if (bareAuthPaths.includes(this.$route.path)) return false;
      const path = this.$route.path || '';
      if (
        path.startsWith('/legislative-watch/')
        || path.startsWith('/action/')
        || path.startsWith('/setup-pack/')
        || path.includes('/secure-upload/')
        || this.$route.name === 'ArtifactComparePrint'
        || this.$route.name === 'BriefingPrint'
      ) {
        return false;
      }
      return true;
    },
  },

  watch: {
    isAuthenticated(newVal) {
      if (newVal) {
        this._startInactivityTracking();
      } else {
        this._stopInactivityTracking();
      }
    },
    showReconnectingOverlay(isActive, wasActive) {
      if (wasActive && !isActive) {
        this.reloadAfterDeployOverlay();
      }
    },
  },

  methods: {
    refreshForOutage() {
      window.location.reload();
    },

    reloadAfterDeployOverlay() {
      const key = 'accessiq_outage_reload';
      const last = Number(sessionStorage.getItem(key) || 0);
      if (Date.now() - last < 15000) return;
      sessionStorage.setItem(key, String(Date.now()));
      window.location.reload();
    },

    onDemoStateChange(e) {
      setDemoContext({ state: e.target.value });
    },

    onDemoCompanyChange(e) {
      setDemoContext({ companyType: e.target.value });
    },

    // ── Inactivity timer ───────────────────────────────────────────
    _startInactivityTracking() {
      this.boundResetInactivity = this._resetInactivityTimer.bind(this);
      window.addEventListener('mousemove', this.boundResetInactivity, { passive: true });
      window.addEventListener('keydown',   this.boundResetInactivity, { passive: true });
      window.addEventListener('click',     this.boundResetInactivity, { passive: true });
      this._resetInactivityTimer();
    },

    _stopInactivityTracking() {
      if (this.boundResetInactivity) {
        window.removeEventListener('mousemove', this.boundResetInactivity);
        window.removeEventListener('keydown',   this.boundResetInactivity);
        window.removeEventListener('click',     this.boundResetInactivity);
      }
      clearTimeout(this.inactivityTimerId);
      clearTimeout(this.warningTimerId);
      this.inactivityTimerId = null;
      this.warningTimerId = null;
      this.showInactivityWarning = false;
    },

    _resetInactivityTimer() {
      clearTimeout(this.inactivityTimerId);
      clearTimeout(this.warningTimerId);
      this.showInactivityWarning = false;

      this.warningTimerId = setTimeout(() => {
        this.showInactivityWarning = true;
        runBeforeSessionLogout();
      }, INACTIVITY_WARNING_MS);

      this.inactivityTimerId = setTimeout(() => {
        this._forceLogout();
      }, INACTIVITY_LOGOUT_MS);
    },

    async _forceLogout() {
      const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      try {
        await runBeforeSessionLogout();
      } catch {
        /* still log out */
      }
      this._stopInactivityTracking();
      auth.logout();
      const safe = safeAppReturnPath(returnPath);
      const target = safe
        ? `/login?reason=session_timeout&redirect=${encodeURIComponent(safe)}`
        : '/login?reason=session_timeout';
      window.location.href = target;
    },

    stayLoggedIn() {
      this._resetInactivityTimer();
    },

    // ── Demo exit modal ────────────────────────────────────────────
    openExitModal() {
      this.exitPassword = '';
      this.exitError = '';
      this.showExitModal = true;
      this.$nextTick(() => {
        this.$refs.exitPasswordInput?.focus();
      });
    },

    cancelExit() {
      this.showExitModal = false;
      this.exitPassword = '';
      this.exitError = '';
    },

    async confirmExit() {
      if (!this.exitPassword) return;
      if (this.exitPassword !== 'DemoAccessIQ!') {
        this.exitError = 'Incorrect password. Please try again.';
        return;
      }
      clearAssumedOrg();
      this.showExitModal = false;
      this.exitPassword = '';
    },

    handleNextVisitor() {
      startNextVisitor();
    },

    boothToolUrl(path) {
      let booth = '304';
      try {
        booth = sessionStorage.getItem('accessiq_booth') || '304';
      } catch { /* ignore */ }
      if (path === '/login') {
        return `/login?booth=${encodeURIComponent(booth)}`;
      }
      return `${path}?booth=${encodeURIComponent(booth)}`;
    },
  },
}
</script>

<style>
.app {
  display: flex;
  min-height: 100vh;
}

/* ── View As top bar ─────────────────────────────────────────── */
.view-as-topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 200;
  min-height: 36px;
  height: 36px;
  background: #008ca6;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 18px;
  font-size: 0.82rem;
  font-weight: 500;
}

.view-as-topbar--demo {
  height: auto;
  min-height: 40px;
  flex-wrap: wrap;
  padding-top: 6px;
  padding-bottom: 6px;
  row-gap: 6px;
}

.demo-divider {
  width: 1px;
  height: 18px;
  background: rgba(255, 255, 255, 0.35);
  margin: 0 4px;
  flex-shrink: 0;
}

.demo-field {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

.demo-select {
  appearance: none;
  -webkit-appearance: none;
  background-color: rgba(255, 255, 255, 0.18);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='white' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: #ffffff;
  border-radius: 6px;
  padding: 3px 28px 3px 10px;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  max-width: 220px;
}

.demo-select option {
  color: #212938;
  background: #ffffff;
}

.view-as-role-chip {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 2px 9px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
}

.view-as-exit-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff;
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.view-as-exit-btn:hover {
  background: rgba(255, 255, 255, 0.28);
}

/* ── Conference booth top bar ─────────────────────────────────── */
.conference-topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 200;
  height: 36px;
  background: #0059b2;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 18px;
  font-size: 0.82rem;
  font-weight: 500;
}

.conference-booth-links {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
}

.conference-booth-links a {
  color: #ffffff;
  text-decoration: none;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.14);
}

.conference-booth-links a:hover {
  background: rgba(255, 255, 255, 0.28);
}

.conference-next-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: none;
  color: #0059b2;
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 0.76rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
}

.conference-next-btn:hover {
  background: #e0efff;
}

/* ── Inactivity warning modal ────────────────────────────────── */
.inactivity-overlay {
  position: fixed;
  inset: 0;
  background: rgba(33, 41, 56, 0.55);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.inactivity-modal {
  background: #ffffff;
  border-radius: 12px;
  padding: 28px 32px 24px;
  width: 380px;
  max-width: calc(100vw - 32px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.inactivity-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.05rem;
  font-weight: 700;
  color: #212938;
}

.inactivity-header .fas {
  color: #0059b2;
  font-size: 1.1rem;
}

.inactivity-body {
  font-size: 0.875rem;
  color: #6b7a99;
  margin: 0;
  line-height: 1.5;
}

.inactivity-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

.inactivity-stay-btn {
  padding: 8px 20px;
  border: none;
  border-radius: 7px;
  background: #0059b2;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.inactivity-stay-btn:hover {
  background: #0073e6;
}

/* ── Build-4: global API outage reconnect overlay ───────────── */
.reconnect-overlay {
  position: fixed;
  inset: 0;
  background: rgba(33, 41, 56, 0.55);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reconnect-modal {
  background: #ffffff;
  border-radius: 12px;
  padding: 28px 32px 24px;
  width: 380px;
  max-width: calc(100vw - 32px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.reconnect-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.05rem;
  font-weight: 700;
  color: #212938;
}

.reconnect-header .fas {
  color: #0059b2;
  font-size: 1.1rem;
}

.reconnect-spinner {
  animation: reconnect-spin 1s linear infinite;
}

@keyframes reconnect-spin {
  to { transform: rotate(360deg); }
}

.reconnect-refresh-btn {
  align-self: stretch;
  margin-top: 4px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background: #0059b2;
  color: #ffffff;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
}

.reconnect-refresh-btn:hover {
  background: #0073e6;
}

.reconnect-body {
  font-size: 0.875rem;
  color: #6b7a99;
  margin: 0;
  line-height: 1.5;
}

/* ── Demo exit password modal ────────────────────────────────── */
.demo-exit-overlay {
  position: fixed;
  inset: 0;
  background: rgba(33, 41, 56, 0.55);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.demo-exit-modal {
  background: #ffffff;
  border-radius: 12px;
  padding: 28px 32px 24px;
  width: 380px;
  max-width: calc(100vw - 32px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.demo-exit-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.05rem;
  font-weight: 700;
  color: #212938;
}

.demo-exit-header .fas {
  color: #008ca6;
  font-size: 1.1rem;
}

.demo-exit-body {
  font-size: 0.875rem;
  color: #6b7a99;
  margin: 0;
  line-height: 1.5;
}

.demo-exit-input {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #d9e2ef;
  border-radius: 7px;
  font-size: 0.9rem;
  color: #212938;
  outline: none;
  transition: border-color 0.15s;
}

.demo-exit-input:focus {
  border-color: #0059b2;
}

.demo-exit-error {
  font-size: 0.8rem;
  color: #c0392b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 5px;
}

.demo-exit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}

.demo-exit-cancel {
  padding: 7px 16px;
  border: 1px solid #d9e2ef;
  border-radius: 7px;
  background: #ffffff;
  color: #6b7a99;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}

.demo-exit-cancel:hover:not(:disabled) {
  background: #f5f8ff;
}

.demo-exit-confirm {
  padding: 7px 18px;
  border: none;
  border-radius: 7px;
  background: #0059b2;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s;
}

.demo-exit-confirm:hover:not(:disabled) {
  background: #0073e6;
}

.demo-exit-confirm:disabled,
.demo-exit-cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
/* ── Main content ────────────────────────────────────────────── */
.main-content {
  flex: 1;
  min-width: 0;
  margin-left: 250px; /* Corresponds to sidebar width */
  transition: margin-left 0.3s;
  padding: var(--spacing-lg);
  background-color: var(--color-surface);
}

/* Push content down when the view-as top bar is active */
.main-content.has-view-as-bar {
  padding-top: calc(var(--spacing-lg) + 36px);
}

body:has(.view-as-topbar--demo) .main-content.has-view-as-bar {
  padding-top: calc(var(--spacing-lg) + 48px);
}

.main-content.has-conference-bar {
  padding-top: calc(var(--spacing-lg) + 36px);
}

/* No sidebar layout for login/unauthenticated pages */
.main-content.no-sidebar {
  margin-left: 0;
  padding: 0; /* Remove padding for login page to allow full width */
}

/* Adjust main content when sidebar is collapsed */
.sidebar.collapsed + .main-content {
  margin-left: 60px; /* Corresponds to collapsed sidebar width */
}

/* Global styles */
body {
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  background: #f8f9fa;
}

* {
  box-sizing: border-box;
}

/* Add any global styles here */
.page-title {
  margin-bottom: 2rem;
  color: #2c3e50;
}

.action-button {
  background-color: var(--color-primary);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.action-button:hover {
  background-color: var(--color-secondary);
}

.error-message {
  background-color: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 4px;
  margin: 1rem 0;
}

.main-nav {
  display: flex;
  align-items: center;
  padding-bottom: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
  border-bottom: 1px solid var(--color-border);
}

.main-nav a {
  margin-right: var(--spacing-md);
  font-weight: 600;
  color: var(--color-text-light);
  transition: color 0.2s;
}

.main-nav a:hover,
.main-nav a.router-link-exact-active {
  color: var(--color-primary);
}

.logout-button {
  margin-left: auto;
  background-color: var(--color-accent);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.logout-button:hover {
  background-color: #c49a1d;
}

@media print {
  /* Hide all global chrome — banners, nav, modals — during any print view */
  .view-as-topbar,
  .nav-sidebar,
  .conference-topbar,
  .demo-exit-overlay,
  .inactivity-overlay {
    display: none !important;
  }
  .app {
    display: block !important;
  }
  .main-content,
  .main-content.has-view-as-bar,
  .main-content.has-conference-bar {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    max-width: none !important;
  }
  html, body {
    background: #ffffff !important;
    width: auto !important;
  }
}

/* Print views load on demand; keep letter portrait for every page. */
@page {
  size: letter portrait;
}
</style>